import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Copy, Send, ExternalLink, Check, X as XIcon, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNELS = [
  { key: "linkedin", label: "LinkedIn" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "instagram_story", label: "IG Story" },
  { key: "x", label: "X" },
  { key: "pinterest", label: "Pinterest" },
  { key: "telegram", label: "Telegram" },
];

const chipStyle = (state) =>
  state === "ok"
    ? { background: "var(--rose-soft)", borderColor: "var(--gold)", color: "var(--camel)" }
    : state === "fail"
    ? { background: "transparent", borderColor: "var(--rose)", color: "var(--rose)" }
    : { background: "transparent", borderColor: "var(--line)", color: "var(--muted-warm)" };

const channelState = (article, key) => {
  if (!article.social_posted_at) return "pending";
  const r = (article.social_results || {})[key] || (key === "facebook" || key === "instagram" ? ((article.social_results || {}).meta || {})[key] : undefined);
  if (!r) return "pending";
  if (r.skipped) return "skipped";
  return r.ok ? "ok" : "fail";
};

const VideoQueuePanel = ({ adminKey, fmt }) => {
  const [queue, setQueue] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const copyTikTok = (q) => {
    navigator.clipboard.writeText(q.caption_tiktok || "").then(() => {
      setCopiedId(q._id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {});
  };
  const load = () => {
    axios.get(`${API}/admin/videos/queue`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => setQueue(r.data.queue || []))
      .catch(() => {});
  };
  useEffect(load, [adminKey]);
  const remove = async (id) => {
    await axios.delete(`${API}/admin/videos/queue/${id}`, { headers: { "X-Admin-Key": adminKey } }).catch(() => {});
    load();
  };
  if (!queue.length) return null;
  const badge = (s) => s === "published"
    ? { label: "✓ veröffentlicht", bg: "#E6F4EA", color: "#1E7D3C" }
    : s === "failed"
      ? { label: "✗ fehlgeschlagen", bg: "#FDEAEA", color: "#B3261E" }
      : { label: "geplant", bg: "var(--rose-soft)", color: "var(--camel)" };
  return (
    <div className="mt-5 rounded-2xl border p-5" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="video-queue-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-lg font-semibold">Video-Warteschlange</h3>
        <span className="text-xs" style={{ color: "var(--muted-warm)" }}>{queue.filter((q) => q.status === "scheduled").length} geplant · Posting täglich 11:30</span>
      </div>
      <div className="mt-3 space-y-2">
        {queue.map((q) => {
          const b = badge(q.status);
          return (
            <div key={q._id} data-testid={`video-queue-item-${q._id}`} className="flex items-center gap-3 rounded-xl border px-3 py-2" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
              <span className="w-20 shrink-0 text-xs font-semibold" style={{ color: "var(--ink)" }}>{fmt ? fmt(q.publish_date) : q.publish_date}</span>
              <span className="min-w-0 flex-1 truncate text-xs" style={{ color: "var(--ink)" }}>{(q.filename || "").replace(".mp4.mp4", "").replace("Kopie von ", "")}</span>
              <span className="hidden text-xs sm:block" style={{ color: "var(--muted-warm)" }}>{(q.channels || []).map((c) => (c === "instagram" ? "IG" : c === "facebook" ? "FB" : "YT")).join("+")}</span>
              {q.file_id && (
                <a data-testid={`video-download-${q._id}`} href={`${API}/videos/${q.file_id}`} target="_blank" rel="noreferrer" className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium no-underline" style={{ borderColor: "var(--line)", color: "var(--ink)", background: "var(--surface)" }}>
                  ⬇ Video
                </a>
              )}
              {q.caption_tiktok && (
                <button data-testid={`video-tiktok-copy-${q._id}`} onClick={() => copyTikTok(q)} className="shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium" style={copiedId === q._id ? { borderColor: "#1E7D3C", color: "#1E7D3C", background: "#E6F4EA" } : { borderColor: "var(--line)", color: "var(--ink)", background: "var(--surface)" }}>
                  {copiedId === q._id ? "✓ kopiert" : "TikTok ⧉"}
                </button>
              )}
              <span className="rounded-full px-2.5 py-1 text-[11px] font-medium" style={{ background: b.bg, color: b.color }} data-testid={`video-status-${q._id}`}>{b.label}</span>
              {q.status === "scheduled" && (
                <button data-testid={`video-delete-${q._id}`} onClick={() => remove(q._id)} className="text-xs underline" style={{ color: "var(--muted-warm)" }}>entfernen</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SOURCE_LABELS = { instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", x: "X", pinterest: "Pinterest", telegram: "Telegram", youtube: "YouTube", youtube_shorts: "YT Shorts", newsletter: "Newsletter" };

const PerformancePanel = ({ adminKey }) => {
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/performance?days=7`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => setPerf(r.data))
      .catch(() => {});
  }, [adminKey]);
  if (!perf) return null;
  return (
    <div className="mt-5 rounded-2xl border p-5" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="performance-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-lg font-semibold">Magazin-Besucher nach Kanal</h3>
        <span className="text-xs" style={{ color: "var(--muted-warm)" }}>letzte 7 Tage · {perf.total} UTM-Besuche</span>
      </div>
      {perf.total === 0 ? (
        <p className="mt-2 text-sm" style={{ color: "var(--muted-warm)" }}>Noch keine getrackten Besuche – Daten sammeln sich, sobald jemand über Social-Links kommt.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {perf.sources.map((s, i) => (
              <span key={i} data-testid={`perf-source-${s.source}`} className="rounded-full border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "var(--gold)", background: "var(--rose-soft)", color: "var(--camel)" }}>
                {SOURCE_LABELS[s.source] || s.source}{s.medium ? ` · ${s.medium}` : ""}: <strong>{s.visits}</strong>
              </span>
            ))}
          </div>
          {perf.top_articles.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium" style={{ color: "var(--muted-warm)" }}>Meistbesuchte Artikel über Social:</p>
              {perf.top_articles.slice(0, 5).map((t, i) => (
                <p key={i} className="mt-1 text-xs" style={{ color: "var(--ink)" }}>{i + 1}. {t.path.replace("/blog/", "")} · {t.visits} Besuche</p>
              ))}
            </div>
          )}
        </>
      )}
      <div className="mt-4 rounded-xl border p-4" style={{ borderColor: "var(--gold)", background: "var(--surface)" }} data-testid="business-performance-block">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">✦ Boutique-Besucher auf /business</p>
          <span className="text-xs font-bold" style={{ color: "var(--camel)" }} data-testid="business-visits-total">{perf.business?.total ?? 0} Besuche</span>
        </div>
        {(perf.business?.sources?.length ?? 0) === 0 ? (
          <p className="mt-1.5 text-xs" style={{ color: "var(--muted-warm)" }}>Noch keine B2B-Besuche getrackt – B2B-Posts und -Videos verlinken ab jetzt mit UTM auf /business.</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {perf.business.sources.map((s, i) => (
              <span key={i} data-testid={`business-source-${s.source}`} className="rounded-full border px-3 py-1.5 text-xs font-medium" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
                {SOURCE_LABELS[s.source] || s.source}: <strong>{s.visits}</strong>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const MagazineTab = ({ adminKey, fmt }) => {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [posting, setPosting] = useState("");
  const [insights, setInsights] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/articles`, { headers: { "X-Admin-Key": adminKey } });
      setArticles(r.data);
      for (const a of r.data.filter((x) => x.social_posted_at).slice(0, 10)) {
        try {
          const ir = await axios.get(`${API}/admin/articles/${a.slug}/insights`, { headers: { "X-Admin-Key": adminKey } });
          setInsights((prev) => ({ ...prev, [a.slug]: ir.data }));
        } catch { /* skip */ }
      }
    } catch {
      setError("Artikel konnten nicht geladen werden.");
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const copyLinkedIn = (a) => {
    const text = `${a.social?.linkedin_de || ""}\n\nhttps://mira-mirror.ai/blog/${a.slug}`;
    navigator.clipboard.writeText(text);
    setCopied(a.slug);
    setTimeout(() => setCopied(""), 2000);
  };

  const postNow = async (a) => {
    if (!window.confirm(`Social-Posts für «${a.title}» jetzt auf allen verbundenen Kanälen veröffentlichen?`)) return;
    setPosting(a.slug);
    try {
      await axios.post(`${API}/admin/articles/${a.slug}/post-social`, {}, { headers: { "X-Admin-Key": adminKey } });
      setTimeout(() => { load(); setPosting(""); }, 8000);
    } catch {
      setError("Posten fehlgeschlagen.");
      setPosting("");
    }
  };

  if (error) return <p className="mt-8 text-sm" style={{ color: "var(--rose)" }}>{error}</p>;
  if (!articles) return <div className="mt-12 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="mt-8" data-testid="magazine-tab">
      <p className="text-sm" style={{ color: "var(--muted-warm)" }}>
        Täglich 06:30 Uhr erscheint ein neuer KI-Artikel, um 11:30 Uhr gehen die Social-Posts raus. {articles.length} KI-Artikel bisher.
      </p>
      <PerformancePanel adminKey={adminKey} />
      <VideoQueuePanel adminKey={adminKey} fmt={(d) => new Date(d).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })} />
      <div className="mt-5 space-y-4">
        {articles.map((a) => (
          <div key={a.slug} className="rounded-2xl border p-5" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid={`magazine-article-${a.slug}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs" style={{ color: "var(--muted-warm)" }}>
                  <span>{a.date}</span>
                  <span className="rounded-full border px-2 py-0.5" style={{ borderColor: "var(--line)" }}>{a.category}{a.audience ? ` · ${a.audience === "women" ? "Damen" : "Herren"}` : ""}</span>
                  {!a.social_posted_at && <span className="flex items-center gap-1"><Clock size={11} /> Social ausstehend (11:30)</span>}
                </div>
                <a href={`/blog/${a.slug}`} target="_blank" rel="noreferrer" className="mt-1.5 flex items-center gap-1.5 font-serif-display text-lg font-semibold hover:underline">
                  {a.title} <ExternalLink size={13} style={{ color: "var(--muted-warm)" }} />
                </a>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`copy-linkedin-${a.slug}`}
                  onClick={() => copyLinkedIn(a)}
                  className="flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors hover:border-[var(--camel)]"
                  style={{ borderColor: "var(--line)" }}
                >
                  {copied === a.slug ? <Check size={13} /> : <Copy size={13} />} LinkedIn-Text
                </button>
                {!a.social_posted_at && (
                  <button
                    data-testid={`post-social-${a.slug}`}
                    onClick={() => postNow(a)}
                    disabled={posting === a.slug}
                    className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium text-white transition-opacity disabled:opacity-60"
                    style={{ background: "var(--camel)" }}
                  >
                    {posting === a.slug ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Jetzt posten
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const st = channelState(a, c.key);
                return (
                  <span key={c.key} data-testid={`channel-${c.key}-${a.slug}`} className="flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium" style={chipStyle(st)}>
                    {st === "ok" ? <Check size={11} /> : st === "fail" ? <XIcon size={11} /> : <Clock size={11} />}
                    {c.label}
                    {st === "skipped" && " (nicht verbunden)"}
                    {st === "fail" && " fehlgeschlagen"}
                  </span>
                );
              })}
            </div>
            {insights[a.slug] && Object.keys(insights[a.slug]).length > 0 && (
              <p className="mt-2.5 text-xs" data-testid={`insights-${a.slug}`} style={{ color: "var(--muted-warm)" }}>
                {insights[a.slug].instagram && `IG: ${insights[a.slug].instagram.reach ?? 0} erreicht · ${insights[a.slug].instagram.likes ?? 0} Likes`}
                {insights[a.slug].facebook && `  ·  FB: ${insights[a.slug].facebook.likes ?? 0} Likes · ${insights[a.slug].facebook.comments ?? 0} Kommentare`}
                {insights[a.slug].x && `  ·  X: ${insights[a.slug].x.views ?? 0} Views · ${insights[a.slug].x.likes ?? 0} Likes`}
              </p>
            )}
          </div>
        ))}
        {articles.length === 0 && <p className="text-sm" style={{ color: "var(--muted-warm)" }}>Noch keine KI-Artikel – der erste erscheint morgen um 06:30 Uhr.</p>}
      </div>
    </div>
  );
};
